import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { MoonStar, Loader2, Bell, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { enablePush, isPushSupported } from "@/lib/push";

const TZ_OPTIONS = [
  ["America/Los_Angeles", "Pacific (Los Angeles)"],
  ["America/Denver", "Mountain (Denver)"],
  ["America/Chicago", "Central (Chicago)"],
  ["America/New_York", "Eastern (New York)"],
  ["UTC", "UTC"],
  ["Europe/London", "London"],
];

const hourLabel = (h) => {
  const period = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${period}`;
};

export default function NotificationSettings({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [start, setStart] = useState(21);
  const [end, setEnd] = useState(8);
  const [tz, setTz] = useState("America/Los_Angeles");
  const [docTz, setDocTz] = useState("America/Chicago");
  const [notifyPush, setNotifyPush] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(true);
  const [busy, setBusy] = useState(false);
  const isLandlord = user?.role === "landlord";

  useEffect(() => {
    if (open && user) {
      setEnabled(!!user.quiet_hours_enabled);
      setStart(Number.isInteger(user.quiet_start) ? user.quiet_start : 21);
      setEnd(Number.isInteger(user.quiet_end) ? user.quiet_end : 8);
      setTz(user.quiet_tz || "America/Los_Angeles");
      setDocTz(user.doc_timezone || "America/Chicago");
      setNotifyPush(user.notify_push !== false);
      setNotifyEmail(user.notify_email !== false);
      setNotifySms(user.notify_sms !== false);
    }
  }, [open, user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/notifications", {
        quiet_hours_enabled: enabled,
        quiet_start: start,
        quiet_end: end,
        quiet_tz: tz,
        doc_timezone: isLandlord ? docTz : undefined,
        notify_push: notifyPush,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      });
      await refreshUser();
      toast.success("Notification settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const enableOnThisDevice = async () => {
    try {
      await enablePush();
      setNotifyPush(true);
      toast.success("Push enabled on this device");
    } catch (e) {
      toast.error(e.message || "Could not enable push on this device");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MoonStar className="h-5 w-5 text-forest" /> Notification Settings
          </DialogTitle>
          <DialogDescription>Choose how LocalLease reaches you — push, email or text — and set quiet hours.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div data-testid="channel-prefs" className="rounded-xl border border-line divide-y divide-line">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-start gap-3">
                <Bell className="h-4 w-4 text-forest mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-ink">Push notifications</p>
                  <p className="text-xs text-mute mt-0.5">Alerts on your installed app / browser.
                    {isPushSupported() && (
                      <button type="button" data-testid="enable-push-device" onClick={enableOnThisDevice} className="ml-1 text-forest hover:underline font-medium">Enable on this device</button>
                    )}
                  </p>
                </div>
              </div>
              <Switch data-testid="pref-push-switch" checked={notifyPush} onCheckedChange={setNotifyPush} />
            </div>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-forest mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-ink">Email</p>
                  <p className="text-xs text-mute mt-0.5">Reminders and updates sent to {user?.email}.</p>
                </div>
              </div>
              <Switch data-testid="pref-email-switch" checked={notifyEmail} onCheckedChange={setNotifyEmail} />
            </div>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-4 w-4 text-forest mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-ink">Text messages (SMS)</p>
                  <p className="text-xs text-mute mt-0.5">{user?.phone ? `Sent to ${user.phone}.` : "Add a phone number to your profile to receive texts."}</p>
                </div>
              </div>
              <Switch data-testid="pref-sms-switch" checked={notifySms} onCheckedChange={setNotifySms} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-line p-4">
            <div>
              <p className="text-sm font-medium text-ink">Quiet hours</p>
              <p className="text-xs text-mute mt-0.5">SMS & email are held during this window and delivered afterward. In-app alerts still show instantly.</p>
            </div>
            <Switch data-testid="quiet-hours-switch" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {enabled && (
            <div className="grid grid-cols-2 gap-3 animate-fade-up opacity-0" style={{ animationDelay: "0ms" }}>
              <div>
                <Label>From</Label>
                <select data-testid="quiet-start" value={start} onChange={(e) => setStart(parseInt(e.target.value))} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  {[...Array(24)].map((_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
              </div>
              <div>
                <Label>Until</Label>
                <select data-testid="quiet-end" value={end} onChange={(e) => setEnd(parseInt(e.target.value))} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  {[...Array(24)].map((_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Time zone</Label>
                <select data-testid="quiet-tz" value={tz} onChange={(e) => setTz(e.target.value)} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  {TZ_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </div>
              <p className="col-span-2 text-xs text-mute">
                Quiet from <span className="font-medium text-ink">{hourLabel(start)}</span> to <span className="font-medium text-ink">{hourLabel(end)}</span>{start === end ? " (no window — disabled)" : ""}.
              </p>
            </div>
          )}
        </div>

        {isLandlord && (
          <div data-testid="doc-tz-section" className="rounded-xl border border-line p-4">
            <p className="text-sm font-medium text-ink">Signed document timezone</p>
            <p className="text-xs text-mute mt-0.5">The date &amp; time stamped on signed leases and signature certificates use this timezone.</p>
            <select
              data-testid="doc-timezone"
              value={docTz}
              onChange={(e) => setDocTz(e.target.value)}
              className="mt-2 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm"
            >
              {TZ_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>
        )}

        <DialogFooter>
          <button data-testid="save-notification-settings" disabled={busy} onClick={save} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Settings
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
